import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientService } from '../services/api';
import ConfirmModal from './ConfirmModal';
import projectsService from '../services/projectsService';
import ClientFileModal from './ClientFileModal';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Calendar,
  X,
  Save,
  Eye,
  ChevronRight,
  Folder,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  FileCheck
} from 'lucide-react';

const ClientsManagement = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [viewingClient, setViewingClient] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [projectsModalClient, setProjectsModalClient] = useState(null);
  const [projectsTab, setProjectsTab] = useState('active'); // 'active' | 'completed' | 'all'
  const [deleteClient, setDeleteClient] = useState(null);
  const queryClient = useQueryClient();

  const toggleRowExpansion = (clientId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) {
        newSet.delete(clientId);
      } else {
        newSet.add(clientId);
      }
      return newSet;
    });
  };

  // Estados do formulário
  const [formData, setFormData] = useState({
    full_name: '',
    apelido: '',
    cpf: '',
    email: '',
    phone: '',
    document_id: '',
    birth_date: '',
    address: ''
  });

  // Estados separados para endereço
  const [addressFields, setAddressFields] = useState({
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });

  const [loadingCep, setLoadingCep] = useState(false);

  // Query para buscar clientes
  const { data: clientsData, isLoading, error, refetch } = useQuery({
    queryKey: ['clients', { search: searchTerm }],
    queryFn: () => clientService.getClients({
      search: searchTerm || undefined,
      limit: 100
    }),
    staleTime: 0, // Sempre considerar os dados como stale para forçar refetch
    cacheTime: 0, // Não manter em cache quando não está sendo usado
  });

  // Normalizar os dados para garantir que seja sempre um array
  const clients = useMemo(() => {
    if (!clientsData) return [];
    const data = clientsData.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && Array.isArray(data.data)) return data.data;
    return [];
  }, [clientsData]);

  // Refetch automático quando a tela ganha foco (para atualizar lista após criar cliente em outro lugar)
  useEffect(() => {
    const handleFocus = () => {
      refetch();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetch]);

  // Mutations
  const createClientMutation = useMutation({
    mutationFn: (data) => clientService.createClient(data),
    onSuccess: () => {
      toast.success('Cliente criado com sucesso!');
      queryClient.invalidateQueries(['clients']);
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar cliente');
    }
  });

  const updateClientMutation = useMutation({
    mutationFn: ({ id, data }) => clientService.updateClient(id, data),
    onSuccess: () => {
      toast.success('Cliente atualizado com sucesso!');
      queryClient.invalidateQueries(['clients']);
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar cliente');
    }
  });

  const deleteClientMutation = useMutation({
    mutationFn: (id) => clientService.deleteClient(id),
    onSuccess: () => {
      toast.success('Cliente deletado com sucesso!');
      queryClient.invalidateQueries(['clients']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar cliente');
    }
  });

  // Função para parsear endereço completo em campos separados
  const parseAddress = (address) => {
    if (!address) {
      return {
        cep: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: ''
      };
    }

    // Tentar extrair CEP (formato: CEP: 00000-000)
    const cepMatch = address.match(/CEP:\s*(\d{5}-?\d{3})/i);
    const cep = cepMatch ? cepMatch[1].replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2') : '';

    // Tentar extrair estado (últimas 2 letras antes do CEP)
    const stateMatch = address.match(/\b([A-Z]{2})\b(?=.*CEP|$)/i);
    const state = stateMatch ? stateMatch[1].toUpperCase() : '';

    // Tentar extrair cidade (antes do estado)
    const cityMatch = address.match(/([^,]+)(?=,\s*[A-Z]{2}\b|CEP)/i);
    const city = cityMatch ? cityMatch[1].trim() : '';

    // Tentar extrair número (nº 123 ou número 123)
    const numberMatch = address.match(/n[º°]\s*(\d+)/i) || address.match(/número\s*(\d+)/i);
    const number = numberMatch ? numberMatch[1] : '';

    // Tentar extrair rua (primeira parte antes do número)
    const streetParts = address.split(/n[º°]|número/i)[0].split(',').map(s => s.trim());
    const street = streetParts[0] || '';

    // Bairro geralmente é a segunda parte antes da cidade
    const neighborhood = streetParts.length > 1 ? streetParts[1] : '';

    // Complemento pode estar em várias posições
    const complement = '';

    return {
      cep,
      street,
      number,
      complement,
      neighborhood,
      city,
      state
    };
  };

  const openModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        full_name: client.full_name || '',
        apelido: client.apelido || '',
        email: client.email || '',
        phone: client.phone || '',
        document_id: client.document_id || '',
        birth_date: client.birth_date ? client.birth_date.split('T')[0] : '',
        address: client.address || ''
      });
      // Parsear endereço em campos separados
      setAddressFields(parseAddress(client.address));
    } else {
      setEditingClient(null);
      setFormData({
        full_name: '',
        apelido: '',
        email: '',
        phone: '',
        document_id: '',
        birth_date: '',
        address: ''
      });
      setAddressFields({
        cep: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
    setFormData({
      full_name: '',
      apelido: '',
      email: '',
      phone: '',
      document_id: '',
      birth_date: '',
      address: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validar nome
    if (!formData.full_name.trim()) {
      toast.error('Nome do cliente é obrigatório');
      return;
    }

    // Validar CPF/CNPJ (opcional - se fornecido, deve ter 11 ou 14 dígitos)
    const cleanDoc = (formData.document_id || formData.cpf || '').replace(/\D/g, '');
    if (cleanDoc && cleanDoc.length !== 11 && cleanDoc.length !== 14) {
      toast.error('CPF ou CNPJ inválido (deve ter 11 ou 14 dígitos)');
      return;
    }

    // Validar apelido
    if (!formData.apelido || formData.apelido.trim() === '') {
      toast.error('Apelido é obrigatório');
      return;
    }

    // Usar document_id como CPF
    const cpfToSubmit = formData.document_id ? formData.document_id : formData.cpf;

    // Construir endereço completo
    const fullAddress = buildFullAddress();
    const submitData = {
      ...formData,
      cpf: cpfToSubmit || null,
      address: fullAddress || formData.address
    };

    if (editingClient) {
      updateClientMutation.mutate({ id: editingClient.id, data: submitData });
    } else {
      createClientMutation.mutate(submitData);
    }
  };

  const handleDelete = (client) => {
    setDeleteClient(client);
  };

  const handleView = (client) => {
    setViewingClient(client);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  // Função para aplicar máscara de telefone
  const maskPhone = (value) => {
    if (!value) return '';
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Aplica máscara baseada no tamanho
    if (numbers.length <= 10) {
      // Telefone fixo: (00) 0000-0000
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    } else {
      // Celular: (00) 00000-0000
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2')
        .replace(/(-\d{4})\d+?$/, '$1');
    }
  };

  // Função para aplicar máscara de CPF/CNPJ
  const maskDocument = (value) => {
    if (!value) return '';
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // CPF: 11 dígitos
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      // CNPJ: 14 dígitos
      return numbers
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
    }
  };

  const handlePhoneChange = (e) => {
    const maskedValue = maskPhone(e.target.value);
    setFormData({ ...formData, phone: maskedValue });
  };

  const handleDocumentChange = (e) => {
    const maskedValue = maskDocument(e.target.value);
    setFormData({ ...formData, document_id: maskedValue });
  };

  // Função para aplicar máscara de CEP
  const maskCep = (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d)/, '$1-$2').replace(/(-\d{3})\d+?$/, '$1');
  };

  // Função para buscar CEP via API ViaCEP
  const fetchCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      toast.error('CEP deve conter 8 dígitos');
      return;
    }

    setLoadingCep(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/utils/cep/${cleanCep}`);
      const data = await response.json();

      if (data.erro) {
        toast.error('CEP não encontrado');
        return;
      }

      // Preencher campos automaticamente
      setAddressFields({
        ...addressFields,
        street: data.logradouro || '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || '',
        complement: data.complemento || ''
      });

      toast.success('CEP encontrado! Preencha o número e complemento se necessário.');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setLoadingCep(false);
    }
  };

  const handleCepChange = (e) => {
    const maskedValue = maskCep(e.target.value);
    setAddressFields({ ...addressFields, cep: maskedValue });
  };

  const handleCepBlur = () => {
    if (addressFields.cep.replace(/\D/g, '').length === 8) {
      fetchCep(addressFields.cep);
    }
  };

  const handleAddressFieldChange = (field, value) => {
    setAddressFields({ ...addressFields, [field]: value });
  };

  // Função para construir endereço completo
  const buildFullAddress = () => {
    const parts = [];
    if (addressFields.street) parts.push(addressFields.street);
    if (addressFields.number) parts.push(`nº ${addressFields.number}`);
    if (addressFields.complement) parts.push(addressFields.complement);
    if (addressFields.neighborhood) parts.push(addressFields.neighborhood);
    if (addressFields.city) parts.push(addressFields.city);
    if (addressFields.state) parts.push(addressFields.state);
    if (addressFields.cep) parts.push(`CEP: ${addressFields.cep}`);
    
    return parts.join(', ');
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Clientes</h1>
          <p className="text-gray-600">Cadastre e gerencie os clientes do sistema</p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Novo Cliente
        </button>
      </div>

      {/* Busca */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por nome, email ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
          />
        </div>
      </div>

      {/* Lista de Clientes */}
      {isLoading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#49943d] mx-auto"></div>
          <p className="mt-2 text-gray-600">Carregando clientes...</p>
        </div>
      ) : error ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-red-600">Erro ao carregar clientes</p>
        </div>
      ) : clients.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Nenhum cliente encontrado</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <colgroup>
                <col className="w-80" />
                <col className="w-24" />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {clients.map((client) => (
                  <React.Fragment key={client.id}>
                    <tr 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleRowExpansion(client.id)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <ChevronRight 
                            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expandedRows.has(client.id) ? 'rotate-90' : ''}`} 
                          />
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {client.apelido ? `${client.apelido} - ${client.full_name}` : client.full_name}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleView(client)}
                            className="text-[#49943d] hover:text-[#3a7630] p-1"
                            title="Visualizar"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {user.role === 'GERENCIA' && (
                            <button
                              onClick={() => {
                                setProjectsModalClient(client);
                                setProjectsTab('active');
                              }}
                              className="text-purple-600 hover:text-purple-800 p-1"
                              title="Arquivo do Cliente (Dossiê & Projetos)"
                            >
                              <Folder className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => openModal(client)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {user.role === 'GERENCIA' && (
                            <button
                              onClick={() => handleDelete(client)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Deletar"
                              disabled={deleteClientMutation.isLoading}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedRows.has(client.id) && (
                      <tr className="bg-gray-50">
                        <td colSpan={2} className="px-4 py-3">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500 text-xs uppercase">Email</span>
                              <p className="text-gray-900 truncate flex items-center gap-1">
                                {client.email ? (
                                  <>
                                    <Mail className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{client.email}</span>
                                  </>
                                ) : '-'}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500 text-xs uppercase">Telefone</span>
                              <p className="text-gray-900 truncate flex items-center gap-1">
                                {client.phone ? (
                                  <>
                                    <Phone className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{client.phone}</span>
                                  </>
                                ) : '-'}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500 text-xs uppercase">Documento</span>
                              <p className="text-gray-900 truncate">{client.document_id || '-'}</p>
                            </div>
                            <div>
                              <span className="text-gray-500 text-xs uppercase">Cadastrado em</span>
                              <p className="text-gray-900 truncate">{formatDate(client.created_at)}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Arquivo do Cliente (Dossiê & Novo Fluxo de Projetos) */}
      {projectsModalClient && (
        <ClientFileModal
          client={projectsModalClient}
          initialTab={projectsTab}
          onClose={() => setProjectsModalClient(null)}
        />
      )}

      {/* Modal de Cadastro/Edição */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between shrink-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                {editingClient ? 'Editar Cliente' : 'Novo Cliente'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="Nome completo do cliente"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Apelido *
                  </label>
                  <input
                    type="text"
                    value={formData.apelido}
                    onChange={(e) => setFormData({ ...formData, apelido: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="Como o cliente será chamado"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="email@exemplo.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="(00) 00000-0000"
                    maxLength={15}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CPF/CNPJ
                  </label>
                  <input
                    type="text"
                    value={formData.document_id}
                    onChange={handleDocumentChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="000.000.000-00"
                    maxLength={18}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Nascimento
                  </label>
                  <input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  />
                </div>

                {/* CEP */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CEP
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={addressFields.cep}
                      onChange={handleCepChange}
                      onBlur={handleCepBlur}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                      placeholder="00000-000"
                      maxLength={9}
                    />
                    <button
                      type="button"
                      onClick={() => fetchCep(addressFields.cep)}
                      disabled={loadingCep || addressFields.cep.replace(/\D/g, '').length !== 8}
                      className="px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {loadingCep ? '...' : 'Buscar'}
                    </button>
                  </div>
                </div>

                {/* Rua */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rua/Logradouro
                  </label>
                  <input
                    type="text"
                    value={addressFields.street}
                    onChange={(e) => handleAddressFieldChange('street', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="Nome da rua, avenida, etc."
                  />
                </div>

                {/* Número e Complemento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número
                  </label>
                  <input
                    type="text"
                    value={addressFields.number}
                    onChange={(e) => handleAddressFieldChange('number', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="123"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complemento
                  </label>
                  <input
                    type="text"
                    value={addressFields.complement}
                    onChange={(e) => handleAddressFieldChange('complement', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="Apto, Bloco, etc."
                  />
                </div>

                {/* Bairro */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bairro
                  </label>
                  <input
                    type="text"
                    value={addressFields.neighborhood}
                    onChange={(e) => handleAddressFieldChange('neighborhood', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="Nome do bairro"
                  />
                </div>

                {/* Cidade e Estado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade
                  </label>
                  <input
                    type="text"
                    value={addressFields.city}
                    onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="Nome da cidade"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado (UF)
                  </label>
                  <select
                    value={addressFields.state}
                    onChange={(e) => handleAddressFieldChange('state', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  >
                    <option value="">Selecione</option>
                    <option value="AC">Acre</option>
                    <option value="AL">Alagoas</option>
                    <option value="AP">Amapá</option>
                    <option value="AM">Amazonas</option>
                    <option value="BA">Bahia</option>
                    <option value="CE">Ceará</option>
                    <option value="DF">Distrito Federal</option>
                    <option value="ES">Espírito Santo</option>
                    <option value="GO">Goiás</option>
                    <option value="MA">Maranhão</option>
                    <option value="MT">Mato Grosso</option>
                    <option value="MS">Mato Grosso do Sul</option>
                    <option value="MG">Minas Gerais</option>
                    <option value="PA">Pará</option>
                    <option value="PB">Paraíba</option>
                    <option value="PR">Paraná</option>
                    <option value="PE">Pernambuco</option>
                    <option value="PI">Piauí</option>
                    <option value="RJ">Rio de Janeiro</option>
                    <option value="RN">Rio Grande do Norte</option>
                    <option value="RS">Rio Grande do Sul</option>
                    <option value="RO">Rondônia</option>
                    <option value="RR">Roraima</option>
                    <option value="SC">Santa Catarina</option>
                    <option value="SP">São Paulo</option>
                    <option value="SE">Sergipe</option>
                    <option value="TO">Tocantins</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors order-2 sm:order-1"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createClientMutation.isLoading || updateClientMutation.isLoading}
                  className="px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 order-1 sm:order-2"
                >
                  {createClientMutation.isLoading || updateClientMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingClient ? 'Atualizar' : 'Cadastrar'} Cliente
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Visualização */}
      {viewingClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Detalhes do Cliente</h2>
              <button
                onClick={() => setViewingClient(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Nome Completo</label>
                  <p className="text-sm text-gray-900 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {viewingClient.full_name}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Apelido</label>
                  <p className="text-sm text-gray-900 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {viewingClient.apelido || '-'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                  <p className="text-sm text-gray-900 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {viewingClient.email || '-'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Telefone</label>
                  <p className="text-sm text-gray-900 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {viewingClient.phone || '-'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">CPF/CNPJ</label>
                  <p className="text-sm text-gray-900 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {viewingClient.document_id || '-'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Data de Nascimento</label>
                  <p className="text-sm text-gray-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {viewingClient.birth_date ? formatDate(viewingClient.birth_date) : '-'}
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Endereço</label>
                  <p className="text-sm text-gray-900 flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5" />
                    {viewingClient.address || '-'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Cadastrado em</label>
                  <p className="text-sm text-gray-900">{formatDate(viewingClient.created_at)}</p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setViewingClient(null);
                    openModal(viewingClient);
                  }}
                  className="px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Editar Cliente
                </button>
                <button
                  onClick={() => setViewingClient(null)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        show={!!deleteClient}
        onConfirm={() => {
          if (deleteClient) {
            deleteClientMutation.mutate(deleteClient.id);
          }
          setDeleteClient(null);
        }}
        onCancel={() => setDeleteClient(null)}
        title="Excluir Cliente"
        message={deleteClient ? `Tem certeza que deseja deletar o cliente "${deleteClient.full_name}"?` : ''}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

// Modal de Projetos do Cliente
const ClientProjectsModal = ({ client, tab, setTab, onClose }) => {
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in_progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pendente',
      'in_progress': 'Em Andamento',
      'completed': 'Concluído',
      'cancelled': 'Cancelado'
    };
    return labels[status] || status;
  };

  // Query para buscar projetos do cliente
  const { data: projectsData, isLoading: loadingProjects } = useQuery({
    queryKey: ['client-projects', client.id, tab],
    queryFn: async () => {
      const statusFilter = tab === 'active' ? 'in_progress' : (tab === 'completed' ? 'completed' : undefined);
      const params = { limit: 100 };
      if (statusFilter) params.status = statusFilter;
      const response = await projectsService.getProjectsByClientId(client.id, params);
      return response;
    }
  });

  const projects = useMemo(() => {
    if (!projectsData?.data) return [];
    const data = projectsData.data;
    if (Array.isArray(data)) return data;
    if (data?.data) return data.data;
    return [];
  }, [projectsData]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Projetos de {client.apelido || client.full_name}</h2>
            <p className="text-purple-100 text-sm">{projects.length} projeto(s) encontrado(s)</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-purple-200 p-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex px-6">
            <button
              onClick={() => setTab('active')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === 'active'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Em Andamento
            </button>
            <button
              onClick={() => setTab('completed')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === 'completed'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Concluídos
            </button>
            <button
              onClick={() => setTab('all')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === 'all'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Todos
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loadingProjects ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12">
              <Folder className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum projeto encontrado para este cliente</p>
            </div>
          ) : (
            <div className="space-y-4">
              {projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  formatCurrency={formatCurrency}
                  formatDate={formatDate}
                  getStatusBadge={getStatusBadge}
                  getStatusLabel={getStatusLabel}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Cartão de Projeto Individual
const ProjectCard = ({ project, formatCurrency, formatDate, getStatusBadge, getStatusLabel }) => {
  const [expanded, setExpanded] = useState(false);

  // Query para buscar entradas previstas
  const { data: expectedIncomes } = useQuery({
    queryKey: ['project-expected-incomes', project.id],
    queryFn: () => projectsService.getProjectById(project.id),
    enabled: expanded
  });

  const totalExpected = expectedIncomes?.data?.project_value || project.project_value || project.estimated_budget || 0;
  const totalPaid = expectedIncomes?.data?.total_received || project.total_received || 0;
  const pendingAmount = totalExpected - totalPaid;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
      {/* Header do Projeto */}
      <div
        className="p-4 cursor-pointer bg-gradient-to-r from-gray-50 to-white"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              project.status === 'completed' ? 'bg-green-100' : 'bg-blue-100'
            }`}>
              {project.status === 'completed' ? (
                <FileCheck className="w-5 h-5 text-green-600" />
              ) : (
                <Folder className="w-5 h-5 text-blue-600" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {project.project_code || `#${project.id}`} - {project.description || project.project_description || 'Sem descrição'}
              </h3>
              <div className="flex items-center gap-3 mt-1">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(project.status)}`}>
                  {getStatusLabel(project.status)}
                </span>
                <span className="text-xs text-gray-500">
                  Criado em {formatDate(project.created_at)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-xs text-gray-500">Valor Total</p>
              <p className="font-bold text-gray-900">{formatCurrency(totalExpected)}</p>
            </div>
            <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </div>
        </div>

        {/* Resumo Financeiro */}
        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Recebido</p>
              <p className="text-sm font-semibold text-green-600">{formatCurrency(totalPaid)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
              <Clock className="w-4 h-4 text-yellow-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Pendente</p>
              <p className="text-sm font-semibold text-yellow-600">{formatCurrency(pendingAmount)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-sm font-semibold text-blue-600">{formatCurrency(totalExpected)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detalhes Expandidos */}
      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <div className="space-y-4">
            {/* Informações do Projeto */}
            <div className="bg-white rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Informações do Projeto
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500 text-xs">ID</span>
                  <p className="font-medium">{project.id}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">Responsável</span>
                  <p className="font-medium">{project.created_by_name || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">Data de Criação</span>
                  <p className="font-medium">{formatDate(project.created_at)}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">Última Atualização</span>
                  <p className="font-medium">{formatDate(project.updated_at)}</p>
                </div>
              </div>
            </div>

            {/* Resumo Financeiro Detalhado */}
            <div className="bg-white rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Resumo Financeiro
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-xs text-green-700">Total Recebido</span>
                  </div>
                  <p className="text-lg font-bold text-green-700">{formatCurrency(totalPaid)}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                    <span className="text-xs text-yellow-700">Valor Pendente</span>
                  </div>
                  <p className="text-lg font-bold text-yellow-700">{formatCurrency(pendingAmount)}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    <span className="text-xs text-blue-700">Valor Total</span>
                  </div>
                  <p className="text-lg font-bold text-blue-700">{formatCurrency(totalExpected)}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-purple-600" />
                    <span className="text-xs text-purple-700">Pagamentos</span>
                  </div>
                  <p className="text-lg font-bold text-purple-700">
                    {expectedIncomes?.data?.payments_count || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Ações */}
            <div className="flex gap-3">
              <a
                href={`/projetos/${project.id}`}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <Folder className="w-4 h-4" />
                Ver Projeto Completo
              </a>
              <a
                href={`/financeiro/projeto/${project.id}`}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <DollarSign className="w-4 h-4" />
                Ver Financeiro
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsManagement;

